import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { API_BASE_URL } from '../utils/api'
import { CustomersIcon, TasksIcon, DollarIcon } from '../components/Icons'
import { UserSecurityBadges } from '../components/UserSecurityBadges'

interface User {
  id: number
  first_name: string
  last_name: string
  email: string
  phone?: string
  language: string
  role: 'admin' | 'broker'
  tier_id?: number | null
  tier_name?: string
  created_at: string
  updated_at: string
  stats: {
    tasks: number
    deals: number
    customers: number
  }
}

const Users: React.FC = () => {
  const { isAdmin } = useAuth()
  const navigate = useNavigate()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'broker' | 'admin'>('broker')
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    if (isAdmin) {
      fetchUsers()
    }
  }, [isAdmin, filter])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      if (!token) return

      const url = filter === 'all' 
        ? `${API_BASE_URL}/api/admin/users`
        : `${API_BASE_URL}/api/admin/users?role=${filter}`

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        setUsers(data.users || [])
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleImpersonate = async (userId: number) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch(`${API_BASE_URL}/api/admin/impersonate/${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        // Store original token to allow switching back (use sessionStorage for security)
        sessionStorage.setItem('original_token', token)
        localStorage.setItem('token', data.token)
        sessionStorage.setItem('is_impersonating', 'true')
        // Reload page to apply new session
        window.location.href = '/dashboard'
      } else {
        const errorData = await response.json()
        alert(`Failed to impersonate user: ${errorData.error || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Error impersonating user:', error)
      alert('Failed to impersonate user')
    }
  }

  const filteredUsers = users.filter(user => 
    user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (!isAdmin) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            You don't have permission to access this page.
          </p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Users Management</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Manage and monitor all mortgage broker accounts
        </p>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex space-x-4">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                filter === 'all'
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-gray-600'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              All Users ({users.length})
            </button>
            <button
              onClick={() => setFilter('broker')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                filter === 'broker'
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-gray-600'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Brokers ({users.filter(u => u.role === 'broker').length})
            </button>
            <button
              onClick={() => setFilter('admin')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                filter === 'admin'
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-gray-600'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Admins ({users.filter(u => u.role === 'admin').length})
            </button>
          </div>
          
          <div className="flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredUsers.map((user) => (
          <div
            key={user.id}
            onClick={() => navigate(`/dashboard/users/${user.id}`)}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-md transition-all group"
          >
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-gray-800 to-gray-900 rounded-full flex items-center justify-center shrink-0">
                <span className="text-white font-semibold text-lg">
                  {user.first_name[0]}{user.last_name[0]}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-gray-700 dark:group-hover:text-gray-200 truncate">
                  {user.first_name} {user.last_name}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{user.email}</p>
                <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full mt-1 ${
                  user.role === 'admin'
                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
                    : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                }`}>
                  {user.role}
                </span>
                {user.tier_name && (
                  <span className="ml-1.5 inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                    {user.tier_name}
                  </span>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="text-center">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center mx-auto mb-1">
                  <CustomersIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <p className="text-base font-semibold text-gray-900 dark:text-white">{user.stats.customers}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Customers</p>
              </div>
              <div className="text-center">
                <div className="w-8 h-8 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center mx-auto mb-1">
                  <DollarIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                </div>
                <p className="text-base font-semibold text-gray-900 dark:text-white">{user.stats.deals}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Deals</p>
              </div>
              <div className="text-center">
                <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center mx-auto mb-1">
                  <TasksIcon className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                </div>
                <p className="text-base font-semibold text-gray-900 dark:text-white">{user.stats.tasks}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Tasks</p>
              </div>
            </div>

            {/* Security badges — stop propagation so clicks on badges don't navigate */}
            <div onClick={e => e.stopPropagation()}>
              <UserSecurityBadges userId={user.id} onSecurityChanged={fetchUsers} />
            </div>

            <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
              Joined {new Date(user.created_at).toLocaleDateString()}
            </p>
          </div>
        ))}
      </div>

      {filteredUsers.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">No users found matching your criteria.</p>
        </div>
      )}

    </div>
  )
}

export default Users 